import '@theme/product-media-swiper';
import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./product-media-swiper').ProductMediaSwiper} [slideshow]
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent]
 * @property {HTMLElement[]} [media]
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.addEventListener('media-swiper:dynamic-append', this.#handleDynamicAppend, { signal });
    this.#observeDeferredImages();
  }

  #controller = new AbortController();

  /** @type {IntersectionObserver | undefined} */
  #deferredMediaObserver;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#deferredMediaObserver?.disconnect();
  }

  /**
   * Promotes deferred media images (beyond the first few) as they approach the viewport.
   * Needed because some in-app webviews ignore native `loading="lazy"` and fetch every image.
   */
  #observeDeferredImages() {
    const images = this.querySelectorAll('img.media-deferred');
    if (!images.length) return;

    if (!('IntersectionObserver' in window)) {
      images.forEach((image) => this.#loadDeferredImage(image));
      return;
    }

    this.#deferredMediaObserver ??= new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.#deferredMediaObserver?.unobserve(entry.target);
          this.#loadDeferredImage(entry.target);
        }
      },
      { rootMargin: '200px 400px' }
    );

    images.forEach((image) => this.#deferredMediaObserver?.observe(image));
  }

  /** @param {Element} image */
  #loadDeferredImage(image) {
    if (!(image instanceof HTMLImageElement) || !image.classList.contains('media-deferred')) return;

    const srcset = image.getAttribute('data-srcset');
    const src = image.getAttribute('data-src');

    if (srcset) {
      image.setAttribute('srcset', srcset);
      image.removeAttribute('data-srcset');
    }

    if (src) {
      image.setAttribute('src', src);
      image.removeAttribute('data-src');
    }

    image.classList.remove('media-deferred');
  }

  /**
   * @returns {import('./product-media-swiper').ProductMediaSwiper | undefined}
   */
  get #carousel() {
    return this.refs.slideshow;
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    const eventProductId = event.detail?.data?.productId;
    const galleryProductId = this.dataset.productId;

    if (eventProductId && galleryProductId && String(eventProductId) !== String(galleryProductId)) {
      return;
    }

    if (this.presentation === 'carousel' && this.#carousel) {
      const variant = event.detail.resource;
      const featuredMediaId = variant?.featured_media?.id;
      this.#carousel.handleVariantChange(variant?.id, featuredMediaId);
      return;
    }

    const source = event.detail.data.html;
    if (!source) return;

    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;

    if (newMediaGallery.dataset.isCustomallProduct == 'false') {
      this.replaceWith(newMediaGallery);
    }
  };

  /**
   * @param {ZoomMediaSelectedEvent} event
   */
  #handleZoomMediaSelected = async (event) => {
    if (this.presentation === 'carousel' && this.#carousel) {
      this.#carousel.slideToIndex(event.detail.index, false);
      return;
    }

    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * @param {CustomEvent} event
   */
  #handleDynamicAppend = (event) => {
    const detail = event.detail;
    if (!detail?.url) return;

    this.#syncZoomDialogSlide(detail);
  };

  /**
   * @param {object} detail
   * @param {string} detail.url
   * @param {string} detail.id
   * @param {'ai' | 'history'} detail.source
   */
  #syncZoomDialogSlide(detail) {
    const zoomDialog = this.refs.zoomDialogComponent;
    if (!zoomDialog) return;

    const slidesContainer =
      zoomDialog.refs?.slidesContainer ||
      zoomDialog.querySelector('[ref="slidesContainer"]');

    if (slidesContainer instanceof HTMLElement) {
      if (slidesContainer.querySelector(`[data-slide-id="${detail.id}"]`)) return;

      const slide = document.createElement('div');
      slide.className =
        'swiper-slide product-media-container product-media-container--image product-media-container--zoomable product-media-container--dynamic';
      slide.dataset.slideId = detail.id;
      slide.dataset.mediaId = '';
      slide.dataset.slideSource = detail.source;
      slide.setAttribute('ref', 'media[]');
      slide.innerHTML = `
        <drag-zoom-wrapper class="product-media__drag-zoom-wrapper">
          <div class="product-media" style="--ratio: 1;">
            <img
              src="${detail.url}"
              alt=""
              class="product-media__image"
              loading="lazy"
              width="1200"
              height="1200"
              ref="image"
              style="object-fit: contain; width: 100%; height: 100%;"
            />
          </div>
        </drag-zoom-wrapper>
      `;

      if (typeof zoomDialog.appendMediaSlide === 'function') {
        zoomDialog.appendMediaSlide(slide);
      } else {
        slidesContainer.appendChild(slide);
      }

      const thumb = document.createElement('div');
      thumb.className = 'swiper-slide zoom-dialog-swiper__thumb';
      thumb.dataset.slideId = detail.id;
      thumb.innerHTML = `<img src="${detail.url}" alt="" loading="lazy" width="110" height="110" style="object-fit: cover; width: 100%; height: 100%;">`;

      if (typeof zoomDialog.appendThumbSlide === 'function') {
        zoomDialog.appendThumbSlide(thumb);
      }

      zoomDialog.refreshSwipers?.();
      return;
    }

    const gallery = zoomDialog.querySelector('.dialog-zoomed-gallery');
    if (!(gallery instanceof HTMLElement)) return;
    if (gallery.querySelector(`[data-slide-id="${detail.id}"]`)) return;

    const li = document.createElement('li');
    li.className =
      'product-media-container product-media-container--image product-media-container--zoomable product-media-container--dynamic';
    li.dataset.slideId = detail.id;
    li.dataset.mediaId = '';
    li.dataset.slideSource = detail.source;
    li.setAttribute('on:click', '/close');
    li.setAttribute('ref', 'media[]');
    li.innerHTML = `
      <drag-zoom-wrapper class="product-media__drag-zoom-wrapper">
        <div class="product-media" style="--ratio: 1;">
          <img
            src="${detail.url}"
            alt=""
            class="product-media__image"
            loading="lazy"
            width="1200"
            height="1200"
            ref="image"
            style="object-fit: contain; width: 100%; height: 100%;"
          />
        </div>
      </drag-zoom-wrapper>
    `;

    gallery.appendChild(li);

    const thumbnails = zoomDialog.querySelector('.dialog-thumbnails-list');
    if (thumbnails instanceof HTMLElement && thumbnails.children.length > 0) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'button button-unstyled dialog-thumbnails-list__thumbnail';
      button.innerHTML = `<img src="${detail.url}" alt="" loading="lazy" width="110" height="110" style="object-fit: cover; width: 100%; height: 100%;">`;
      button.addEventListener('click', () => {
        const index = Array.from(gallery.children).indexOf(li);
        if (index >= 0) {
          zoomDialog.handleThumbnailClick(index);
        }
      });
      thumbnails.appendChild(button);
    }
  }

  /**
   * @param {number} index
   * @param {PointerEvent} event
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * @param {string} url
   */
  appendAiSlide(url) {
    if (!url || this.presentation !== 'carousel') return null;
    return this.#carousel?.appendAiSlide(url) ?? null;
  }

  /**
   * @param {string} url
   * @param {string} [id]
   */
  appendHistorySlide(url, id) {
    if (!url || this.presentation !== 'carousel') return null;
    return this.#carousel?.appendHistorySlide(url, id) ?? null;
  }

  get swiper() {
    return this.#carousel;
  }

  get slideshow() {
    return this.#carousel;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
